import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FiLock, FiMail, FiShield } from "react-icons/fi";
import { auth } from "../config/supabase";

const AdminLogin = () => {
  console.log("AdminLogin component rendering");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Verify session on component mount.
  // Important: if the URL contains a password-recovery token, route the user to
  // /reset-password instead of auto-logging them into the dashboard. Without
  // this, the recovery link silently creates a session and the user lands on
  // the dashboard with their old password still in place.
  useEffect(() => {
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const isRecovery = hash.includes("type=recovery");
    if (isRecovery) {
      console.log("AdminLogin: recovery token detected → routing to /reset-password");
      navigate("/reset-password" + hash, { replace: true });
      return;
    }

    // Also listen for the PASSWORD_RECOVERY event in case the token is
    // processed asynchronously after mount.
    const unsubscribe = auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password", { replace: true });
      }
    });

    const checkAuth = async () => {
      try {
        console.log("AdminLogin: Checking if user is already authenticated");

        // Check if user is already authenticated
        const { data } = await auth.getSession();

        if (data.session) {
          // Check if the user is an admin
          const isAdmin = await auth.isAdmin();

          if (isAdmin) {
            // User is already logged in and is an admin
            console.log("AdminLogin: Valid admin session, redirecting to dashboard");
            navigate("/admin-dashboard");
          } else {
            // User is logged in but not an admin
            console.log("AdminLogin: User is not an admin, signing out");
            await auth.signOut();
            setIsVerifying(false);
          }
        } else {
          // No active session
          console.log("AdminLogin: No active session found");
          setIsVerifying(false);
        }
      } catch (error) {
        console.error("AdminLogin: Session verification error:", error);
        await auth.signOut(); // Just to be safe
        setIsVerifying(false);
      }
    };

    checkAuth();
    return unsubscribe;
  }, [navigate]);

  // Send a password recovery email with the redirect pointed at /reset-password
  // so the user lands on a real password-change form instead of getting silently
  // auto-logged-in to the dashboard.
  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Enter your email above first, then click 'Forgot password'");
      return;
    }
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: resetError } = await auth.resetPasswordForEmail(email, redirectTo);
      if (resetError) {
        toast.error(resetError.message || "Could not send recovery email");
        setError(resetError.message);
      } else {
        toast.success("Recovery email sent, check your inbox (and spam folder)");
      }
    } catch (err) {
      toast.error("Could not send recovery email");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle login form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log("AdminLogin: Attempting login with:", { email });
      
      // Sign in with Supabase Auth
      const { data, error: signInError } = await auth.signIn(email, password);
      
      if (signInError) {
        throw signInError;
      }
      
      console.log("AdminLogin: Login successful, verifying admin status");
      
      // Check if user is an admin
      const isAdmin = await auth.isAdmin();
      
      if (isAdmin) {
        toast.success("Login successful");
        console.log("AdminLogin: Admin status confirmed, redirecting to dashboard");
        navigate("/admin-dashboard");
      } else {
        // User is not an admin, sign them out
        console.error("AdminLogin: User is not an admin");
        await auth.signOut();
        setError("This account does not have admin access");
        toast.error("Access denied");
      }
    } catch (error) {
      console.error("AdminLogin: Login error:", error);
      
      let errorMessage = "Login failed. Please check your credentials.";
      
      if (error.message) {
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = "Invalid email or password. Please try again.";
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = "Please check your email and confirm your account.";
        } else if (error.message.includes('Too many requests')) {
          errorMessage = "Too many login attempts. Please try again later.";
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };
  
  // Show loading until token verification completes
  if (isVerifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 rounded-full border-4 border-white border-t-transparent animate-spin mb-3"></div>
          <div className="text-white">Verifying session...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="bg-[#121212] rounded-xl shadow-lg border border-white/20 overflow-hidden max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="bg-white/10 p-4 rounded-full">
            <FiShield className="text-white text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> Administration
          </h1>
          <p className="text-gray-400 mt-1">Sign in to access the admin dashboard</p>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-900 rounded-lg text-red-200 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#0a0a0a] block w-full pl-10 pr-3 py-3 border border-gray-800 rounded-lg focus:ring-white focus:border-white text-white"
                  placeholder="admin@lasmari.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0a0a0a] block w-full pl-10 pr-3 py-3 border border-gray-800 rounded-lg focus:ring-white focus:border-white text-white"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-black bg-white hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Sign In"
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                className="text-sm text-gray-400 hover:text-white underline underline-offset-2 disabled:opacity-50"
              >
                Forgot password?
              </button>
            </div>
          </div>
        </form>
        
        <div className="mt-8 pt-6 border-t border-gray-800">
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-4">
              Need admin access?{" "}
              <Link
                to="/admin-register"
                className="text-white hover:text-white"
              >
                Request account here
              </Link>
            </p>
            <p className="text-xs text-gray-500 mb-3">
              <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> Administration
            </p>
            <div className="text-xs text-white bg-white/20 p-3 rounded-lg border border-white/30">
              <p className="font-medium mb-1">🔐 Secure Authentication</p>
              <p>Powered by Supabase Auth</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 